import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import api, { getProductSyncStatus } from '../../services/api';
import ServerDataTable from '../ServerDataTable/ServerDataTable';
import ConfirmDialog from '../ConfirmDialog';
import StatusBadge from '../StatusBadge';

export default function FieldMappingTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const DEFAULT_PAGE_SIZE = 10;

  // State for ServerDataTable
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });

  // State for field mappings
  const [editingId, setEditingId] = useState(null);
  const [editedSourceField, setEditedSourceField] = useState('');
  const [editedTargetField, setEditedTargetField] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newSourceField, setNewSourceField] = useState('');
  const [newTargetField, setNewTargetField] = useState('');
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sourceFieldError, setSourceFieldError] = useState('');
  const [targetFieldError, setTargetFieldError] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [isAddingSaving, setIsAddingSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const updateURLWithParams = (currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams(location.search);

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());

    // Handle search term - add if not empty, remove if empty
    params.set('search', currentSearchTerm || '');

    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(urlPageIndex);
    }
    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }
    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }
  }, [location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm?.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateURLWithParams(0, pageSize, value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    updateURLWithParams(0, pageSize, '');
  };

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate the cache first to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['productFieldMappings'] });

      // Force refetch
      const result = await refetch();

      if (result.error) {
        throw result.error;
      }

    } catch (error) {
      toast.error(`Failed to refresh field mappings: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch field mappings
  const {
    data: mappingsResponse,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['productFieldMappings', debouncedSearchTerm, pageIndex, pageSize],
    queryFn: async () => {
      const response = await api.get('/product-sync/field-mappings', {
        params: {
          search: debouncedSearchTerm || '',
          limit: pageSize,
          offset: pageIndex * pageSize
        }
      });
      return response.data;
    }
  });

  // Sync status
  const { data: syncStatusData, isLoading: isLoadingSyncStatus } = useQuery({
    queryKey: ['productFieldMappingsSyncStatus'],
    queryFn: async () => {
      const response = await getProductSyncStatus();
      return response.data;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: 5000 // Poll every 5 seconds
  });

  // Extract data and pagination info
  const mappingsData = mappingsResponse?.data || [];
  const totalCount = mappingsResponse?.pagination?.total || 0;
  const syncInProgress = Boolean(syncStatusData?.data?.isRunning);

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithParams(newPage, pageSize, searchTerm);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithParams(0, newPageSize, searchTerm);
  };

  // Handle sorting
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  // Edit handlers
  const handleStartEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditedSourceField(mapping.source_field);
    setEditedTargetField(mapping.target_field);
    setSourceFieldError('');
    setTargetFieldError('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedSourceField('');
    setEditedTargetField('');
    setSourceFieldError('');
    setTargetFieldError('');
  };

  const validateSourceField = (value) => {
    if (!value) {
      setSourceFieldError('Source field is required');
      return false;
    }

    setSourceFieldError('');
    return true;
  };

  const validateTargetField = (value, currentMapping = null) => {
    if (!value) {
      setTargetFieldError('Target field is required');
      return false;
    }

    const isDuplicate = mappingsData.some(mapping =>
      mapping.target_field === value && mapping.id !== (currentMapping?.id || null)
    );

    if (isDuplicate) {
      setTargetFieldError('This target field is already mapped');
      return false;
    }

    setTargetFieldError('');
    return true;
  };

  const handleSaveEdit = async () => {
    // Validation
    const currentMapping = mappingsData.find(m => m.id === editingId);
    const isSourceValid = validateSourceField(editedSourceField);
    if (!isSourceValid) {
      return;
    }
    const isTargetValid = validateTargetField(editedTargetField, currentMapping);
    if (!isTargetValid) {
      return;
    }

    setSavingId(editingId);
    try {
      await api.put(`/product-sync/field-mappings/${editingId}`, {
        source_field: editedSourceField.trim(),
        target_field: editedTargetField.trim()
      });

      toast.success('Field mapping updated successfully');
      queryClient.invalidateQueries({ queryKey: ['productFieldMappings'] });

      setEditingId(null);
      setEditedSourceField('');
      setEditedTargetField('');
      setSourceFieldError('');
      setTargetFieldError('');
    } catch (error) {
      if (error.response?.status === 423) {
        toast.error('Cannot update field mapping while product sync is in progress');
      } else {
        toast.error(`Failed to update field mapping: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setSavingId(null);
    }
  };

  // Add new mapping handlers
  const handleStartAddNew = () => {
    setIsAddingNew(true);
    setNewSourceField('');
    setNewTargetField('');
    setSourceFieldError('');
    setTargetFieldError('');
    setPageIndex(0);
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setNewSourceField('');
    setNewTargetField('');
    setSourceFieldError('');
    setTargetFieldError('');
  };

  const handleSaveNew = async () => {
    // Validation
    const isSourceValid = validateSourceField(newSourceField);
    if (!isSourceValid) {
      return;
    }
    const isTargetValid = validateTargetField(newTargetField);
    if (!isTargetValid) {
      return;
    }

    setIsAddingSaving(true);
    try {
      await api.post('/product-sync/field-mappings/single', {
        source_field: newSourceField.trim(),
        target_field: newTargetField.trim()
      });

      toast.success('Field mapping created successfully');
      queryClient.invalidateQueries({ queryKey: ['productFieldMappings'] });

      setIsAddingNew(false);
      setNewSourceField('');
      setNewTargetField('');
      setSourceFieldError('');
      setTargetFieldError('');
    } catch (error) {
      if (error.response?.status === 423) {
        toast.error('Cannot create field mapping while product sync is in progress');
      } else {
        toast.error(`Failed to create field mapping: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setIsAddingSaving(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      setDeletingId(deleteId);
      try {
        await api.delete(`/product-sync/field-mappings/${deleteId}`);

        toast.success('Field mapping deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['productFieldMappings'] });
      } catch (error) {
        if (error.response?.status === 423) {
          toast.error('Cannot delete field mapping while product sync is in progress');
        } else if (error.response?.status === 400 && error.response?.data?.message === 'Cannot delete required field mapping') {
          toast.error('Cannot delete required field mapping');
        } else {
          toast.error(`Failed to delete field mapping: ${error.response?.data?.message || error.message}`);
        }
      } finally {
        setDeletingId(null);
      }
    }
    setConfirmDeleteOpen(false);
    setDeleteId(null);
  };

  // Reset to default
  const handleResetToDefault = async () => {
    setIsResetting(true);
    try {
      await api.resetProductFieldMappingsViaQueue();

      toast.success('Field mapping reset job has been queued successfully');
      queryClient.invalidateQueries({ queryKey: ['productFieldMappings'] });
      setConfirmResetOpen(false);
    } catch (error) {
      if (error?.response?.status === 423) {
        toast.error('Cannot reset field mappings while product sync is in progress');
      } else {
        toast.error(`Failed to queue field mapping reset job: ${error?.response?.data?.message || error.message}`);
      }
    } finally {
      setIsResetting(false);
    }
  };

  // Define columns for ServerDataTable
  const columns = [
    {
      field: 'serialNumber',
      headerName: 'S.No.',
      width: 80,
      sortable: false,
      renderCell: ({ rowIndex }) => {
        const row = tableData[rowIndex];
        if (row?.id === 'new') return 'New';
        return rowIndex + 1 + (pageIndex * pageSize);
      }
    },
    {
      field: 'source_field',
      headerName: 'ERP Source Field',
      width: 250,
      sortable: true,
      renderCell: ({ value, row }) => {
        if (row.id === 'new') {
          return (
            <TextField
              fullWidth
              size='small'
              placeholder='e.g., item_freeform'
              value={newSourceField}
              onChange={(e) => setNewSourceField(e.target.value)}
              error={!!sourceFieldError}
              helperText={sourceFieldError}
              disabled={isRefreshing}
            />
          );
        }
        if (editingId === row.id) {
          return (
            <TextField
              fullWidth
              size='small'
              value={editedSourceField}
              onChange={(e) => setEditedSourceField(e.target.value)}
              error={!!sourceFieldError}
              helperText={sourceFieldError}
              disabled={isRefreshing}
            />
          );
        }
        return value;
      }
    },
    {
      field: 'target_field',
      headerName: 'Shopify Target Field',
      width: 250,
      sortable: true,
      renderCell: ({ value, row }) => {
        if (row.id === 'new') {
          return (
            <TextField
              fullWidth
              size='small'
              placeholder='Enter Target Field'
              value={newTargetField}
              onChange={(e) => setNewTargetField(e.target.value)}
              error={!!targetFieldError}
              helperText={targetFieldError}
              disabled={Boolean(isRefreshing)}
            />
          );
        }
        if (editingId === row.id) {
          return (
            <TextField
              fullWidth
              size='small'
              value={editedTargetField}
              onChange={(e) => setEditedTargetField(e.target.value)}
              error={!!targetFieldError}
              helperText={targetFieldError}
              disabled={Boolean(isRefreshing) || Boolean(row.is_required)}
              InputProps={{
                startAdornment: row.is_required ? (
                  <LockIcon fontSize='small' color='action' sx={{ mr: 1 }} />
                ) : null
              }}
            />
          );
        }
        return (
          <Box display='flex' alignItems='center' gap={1}>
            {value}
            {row.is_required ? <LockIcon fontSize='small' color='action' /> : null}
          </Box>
        );
      }
    },
    {
      field: 'is_required',
      headerName: 'Required',
      width: 100,
      sortable: true,
      renderCell: ({ value }) => {
        return (
          <StatusBadge customLabel={value ? 'Yes' : 'No'} customColor={value ? 'error' : 'success'} customize={true} />
        );
      }
    }
  ];

  if (isAdmin) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.id === 'new') {
          return (
            <Box display='flex' gap={1}>
              <Tooltip title='Save'>
                <span>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={handleSaveNew}
                    disabled={isAddingSaving || isRefreshing}
                  >
                    {isAddingSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Cancel'>
                <span>
                  <IconButton
                    size='small'
                    color='default'
                    onClick={handleCancelAddNew}
                    disabled={isAddingSaving || isRefreshing}
                  >
                    <CancelIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }
        if (editingId === row.id) {
          return (
            <Box display='flex' gap={1}>
              <Tooltip title='Save'>
                <span>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={handleSaveEdit}
                    disabled={Boolean(savingId === row.id) || isRefreshing || isAddingSaving || isResetting}
                  >
                    {savingId === row.id ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Cancel'>
                <span>
                  <IconButton
                    size='small'
                    color='default'
                    onClick={handleCancelEdit}
                    disabled={Boolean(savingId === row.id) || isRefreshing || isAddingSaving || isResetting}
                  >
                    <CancelIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box display='flex' gap={1}>
            <Tooltip title={syncInProgress ? 'Cannot edit while sync is in progress' : 'Edit'}>
              <span>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={() => handleStartEdit(row)}
                  disabled={!isAdmin || isAddingNew || Boolean(editingId !== null) || Boolean(deletingId === row.id) || isRefreshing || isAddingSaving || isResetting || syncInProgress}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.is_required ? 'Cannot delete required field' : syncInProgress ? 'Cannot delete while sync is in progress' : 'Delete'}>
              <span>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => handleDeleteClick(row.id)}
                  disabled={!isAdmin || isAddingNew || Boolean(editingId !== null) || Boolean(deletingId === row.id) || isRefreshing || isAddingSaving || isResetting || Boolean(row.is_required) || syncInProgress}
                >
                  {deletingId === row.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    });
  }

  // Prepare data for table (add new row if adding)
  const tableData = isAddingNew ? [
    {
      id: 'new',
      source_field: newSourceField,
      target_field: newTargetField,
      is_required: false,
      isNew: true
    },
    ...(mappingsData || [])
  ] : (mappingsData || []);

  // For server-side search, we don't need client-side pagination
  // The data is already paginated and filtered on the server
  const paginatedData = tableData;

  // Adjust total count if we're adding a new row
  const adjustedTotalCount = isAddingNew ? totalCount + 1 : totalCount;

  // Calculate which rows are loading (skeleton effect)
  const loadingRows = [];
  if (savingId) {
    loadingRows.push(savingId);
  }
  if (deletingId) {
    loadingRows.push(deletingId);
  }
  if (isAddingSaving && isAddingNew) {
    loadingRows.push('new');
  }

  return (
    <Box>
      {syncInProgress && isAdmin && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Product synchronization is currently in progress. Field mapping updates are disabled until the sync completes.
        </Alert>
      )}
      <ServerDataTable
        data={paginatedData}
        columns={columns}
        sortConfig={sortConfig}
        onSort={handleSort}
        totalRows={adjustedTotalCount}
        currentPage={pageIndex}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={isLoading || isRefreshing || isLoadingSyncStatus}
        isError={isError}
        error={error}
        noDataMessage='No field mappings found. Add a mapping or reset to default.'
        totalPages={Math.ceil(adjustedTotalCount / pageSize)}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearch}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by source or target field'
        searchSize='small'
        loadingRows={loadingRows}
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isLoading || isRefreshing || isResetting || isAddingNew || isAddingSaving || Boolean(savingId) || Boolean(deletingId)}>
              Refresh
            </Button>
            {isAdmin ? (
              <>
                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleStartAddNew}
                  disabled={!isAdmin || isLoading || isAddingNew || isRefreshing || isResetting || isAddingSaving || Boolean(savingId) || Boolean(deletingId) || syncInProgress}
                  title={syncInProgress ? 'Cannot add mapping while sync is in progress' : 'Add new field mapping'}
                  sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
                >
                  Add
                </Button>
                <Button
                  variant='contained'
                  color='warning'
                  startIcon={<RestoreIcon />}
                  onClick={() => setConfirmResetOpen(true)}
                  disabled={!isAdmin || isLoading || isRefreshing || isResetting || isAddingNew || isAddingSaving || Boolean(savingId) || Boolean(deletingId) || syncInProgress}
                  title={syncInProgress ? 'Cannot reset mappings while sync is in progress' : 'Reset to default mappings'}
                  sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
                >
                  Reset
                </Button>
              </>
            ) : (<></>)}
          </Box>
        }
      />

      {/* Confirm Reset Dialog */}
      <ConfirmDialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        onCancel={() => setConfirmResetOpen(false)}
        onConfirm={handleResetToDefault}
        confirmColor='warning'
        title='Reset Field Mappings'
        message='Are you sure you want to reset all field mappings to their default values? This action cannot be undone.'
        confirmText='Reset'
        cancelText='Cancel'
        severity='warning'
        isLoading={isResetting}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmColor='error'
        title='Delete Field Mapping'
        message='Are you sure you want to delete this field mapping? This action cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        severity='warning'
        isLoading={Boolean(deletingId)}
      />
    </Box>
  );
}
